import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FiChevronDown } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { PrisonersOfGeographyMetadata } from './blog/PrisonersOfGeography';
import { WeaponsOfMathDestructionMetadata } from './blog/WeaponsOfMathDestruction';
import { MindfckMetadata } from './blog/Mindfck';
import { ChipWarMetadata } from './blog/ChipWar';
import { HowToLieWithStatisticsMetadata } from './blog/HowToLieWithStatistics';
import { TheSixthExtinctionMetadata } from './blog/TheSixthExtinction';
import { HowTheWorldReallyWorksMetadata } from './blog/HowTheWorldReallyWorks';
import { HappinessHypothesisMetadata } from './blog/HappinessHypothesis';
import { WhereGoodIdeasComeFromMetadata } from './blog/WhereGoodIdeasComeFrom';
import { TheWorldForSaleMetadata } from './blog/TheWorldForSale';
import { ZeroToOneMetadata } from './blog/ZeroToOne';
import { TalkingToStrangersMetadata } from './blog/TalkingToStrangers';
import { InvisibleWomenMetadata } from './blog/InvisibleWomen';
import { OutliersMetadata } from './blog/Outliers';
import { FooledByRandomnessMetadata } from './blog/FooledByRandomness';
import { TheMythOfSisyphusMetadata } from './blog/TheMythOfSisyphus';
import { GunsGermsSteelMetadata } from './blog/GunsGermsAndSteel';
import { CourageMetadata } from './blog/TheCourageToBeDisliked';

const BlogContainer = styled.div`
  margin-top: -4.5rem;
  padding: 10.5rem 1.5rem 7rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgb(var(--bg));
  color: rgb(var(--fg) / 0.92);
  min-height: 100vh;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 10% 0%, rgba(69, 82, 109, 0.18), transparent 40%),
      radial-gradient(circle at 90% 10%, rgba(107, 120, 139, 0.1), transparent 35%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 9.5rem 1rem 5.5rem;
  }
`;

const BlogIntro = styled.div`
  width: 100%;
  max-width: 920px;
  text-align: center;
  margin-bottom: 1.8rem;
`;

const PageTitle = styled.h1`
  margin: 0 0 0.7rem 0;
  font-size: 2.7rem;
  line-height: 1.08;
  letter-spacing: -0.03em;
  color: rgb(var(--fg) / 0.95);
  font-weight: 800;

  span {
    color: rgb(var(--gray));
  }

  @media (max-width: 768px) {
    font-size: 2.05rem;
  }
`;

const IntroText = styled.p`
  margin: 0 auto;
  max-width: 760px;
  font-size: 1rem;
  line-height: 1.85;
  color: rgb(var(--gray) / 0.7);

  @media (max-width: 768px) {
    font-size: 0.94rem;
    line-height: 1.75;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 360px));
  justify-content: center;
  gap: 1.5rem;
  width: 100%;
  max-width: 1220px;
  margin-top: 1.2rem;
  position: relative;
  z-index: 1;
  align-content: start;
  align-items: stretch;
  overflow-x: hidden;
  padding-bottom: 2.25rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(260px, 320px));
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    justify-content: center;
    padding-bottom: 1.5rem;
  }
`;

const BlogCard = styled.div`
  background: rgb(var(--fg) / 0.035);
  border-radius: 18px;
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, background 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  height: 100%;
  flex-direction: column;
  border: 1px solid rgb(var(--fg) / 0.06);
  position: relative;
  backdrop-filter: blur(20px) saturate(1.1);
  -webkit-backdrop-filter: blur(20px) saturate(1.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 32px 64px -16px rgb(0 0 0 / calc(0.6 * var(--shadow-mul)));
    border-color: rgb(var(--fg) / 0.14);
    background: rgb(var(--fg) / 0.055);
  }
`;

const BlogImage = styled.img`
  width: 100%;
  aspect-ratio: 16 / 9;
  height: auto;
  object-fit: cover;
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;
  display: block;
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s ease;

  ${BlogCard}:hover & {
    filter: brightness(1) saturate(1);
  }
`;

const BlogContent = styled.div`
  padding: 1.25rem 1.25rem 1.35rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  min-height: 0;
`;

const BlogTitle = styled.h2`
  font-size: 1.35rem;
  margin-bottom: 0.55rem;
  color: rgb(var(--fg) / 0.92);
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.02em;
  transition: color 0.3s;
  min-height: 3.24rem;

  ${BlogCard}:hover & {
    color: rgb(var(--gray));
  }
`;

const BlogDescription = styled.p`
  font-size: 0.96rem;
  line-height: 1.75;
  color: rgb(var(--gray) / 0.7);
  margin-bottom: 1rem;
  flex-grow: 1;
`;

const DatePublished = styled.p`
  font-size: 0.88rem;
  color: rgb(var(--gray) / 0.45);
  margin-top: auto;
  text-align: right;
  font-style: italic;
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  width: 100%;
  max-width: 1080px;
  position: relative;
  z-index: 50;
  padding: 1.15rem 1.35rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 22px;
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  backdrop-filter: blur(20px) saturate(1.1);
  -webkit-backdrop-filter: blur(20px) saturate(1.1);

  @media (max-width: 768px) {
    gap: 1.15rem;
    padding: 0.95rem;
    border-radius: 18px;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  z-index: 60;
`;

const DropdownButton = styled.button`
  background: rgb(var(--fg) / 0.06);
  color: rgb(var(--fg) / 0.9);
  border: 1px solid rgb(var(--fg) / 0.14);
  padding: 0.78rem 1rem;
  border-radius: 999px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 320px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  max-width: 90vw;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgb(var(--fg) / 0.28);
    background: rgb(var(--fg) / 0.09);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 3.2rem;
  left: 0;
  background: rgb(var(--bg) / 0.92);
  border-radius: 16px;
  box-shadow: 0 24px 56px -8px rgb(0 0 0 / calc(0.6 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.08);
  padding: 0.5rem;
  z-index: 70;
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  width: 320px;
  max-width: 90vw;
  max-height: 320px;
  overflow-y: auto;
  overflow-x: hidden;
  backdrop-filter: blur(28px) saturate(1.3);
  -webkit-backdrop-filter: blur(28px) saturate(1.3);
`;

const DropdownSearchWrapper = styled.div`
  position: sticky;
  top: -0.5rem;
  background: rgb(var(--bg) / 0.95);
  padding: 0.5rem 0 0.55rem 0;
  margin: -0.5rem -0.5rem 0.5rem -0.5rem;
  z-index: 2;
  border-bottom: 1px solid rgb(var(--fg) / 0.06);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
`;

const DropdownSearch = styled.input`
  width: calc(100% - 1rem);
  margin: 0 0.5rem;
  padding: 0.72rem 0.85rem;
  border-radius: 12px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-sizing: border-box;
  background: rgb(var(--fg) / 0.04);
  color: rgb(var(--fg) / 0.92);
  font-size: 0.9rem;

  &::placeholder {
    color: rgb(var(--gray) / 0.45);
  }

  &:focus {
    outline: none;
    border-color: rgb(var(--fg) / 0.24);
    background: rgb(var(--fg) / 0.06);
  }
`;

const DropdownItem = styled.div`
  padding: 0.65rem 0.9rem;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  color: rgb(var(--gray) / 0.7);
  font-size: 0.9rem;

  &:hover {
    background-color: rgb(var(--fg) / 0.05);
    color: rgb(var(--fg) / 0.92);
  }
`;

const ClearButton = styled.button`
  background: rgba(239, 107, 107, 0.12);
  color: rgba(255, 180, 180, 0.9);
  border: 1px solid rgba(239, 107, 107, 0.26);
  padding: 0.6rem 1rem;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.25s ease, background 0.25s ease, border-color 0.25s ease;
  margin-top: 0.2rem;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  &:hover {
    transform: translateY(-1px);
    background: rgba(239, 107, 107, 0.18);
    border-color: rgba(239, 107, 107, 0.42);
  }
`;

const Blog = () => {
  const navigate = useNavigate();
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const tagDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  const blogsData = [
    PrisonersOfGeographyMetadata,
    WeaponsOfMathDestructionMetadata,
    MindfckMetadata,
    ChipWarMetadata,
    HowToLieWithStatisticsMetadata,
    TheSixthExtinctionMetadata,
    HowTheWorldReallyWorksMetadata,
    HappinessHypothesisMetadata,
    WhereGoodIdeasComeFromMetadata,
    TheWorldForSaleMetadata,
    ZeroToOneMetadata,
    TalkingToStrangersMetadata,
    InvisibleWomenMetadata,
    OutliersMetadata,
    FooledByRandomnessMetadata,
    TheMythOfSisyphusMetadata,
    GunsGermsSteelMetadata,
    CourageMetadata
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setIsTagDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCardClick = (path) => {
    navigate(path);
  };

  const handleTagSelect = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
    setIsTagDropdownOpen(false);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory((prev) => (prev === category ? '' : category));
    setIsCategoryDropdownOpen(false);
  };

  const handleClearFilters = () => {
    setSelectedTags([]);
    setSelectedCategory('');
  };

  const filteredBlogs = blogsData
    .filter((blog) =>
      selectedTags.length === 0
        ? true
        : blog.tags && selectedTags.every((tag) => blog.tags.includes(tag))
    )
    .filter((blog) => (selectedCategory === '' ? true : blog.category === selectedCategory))
    .sort((a, b) => new Date(b.datePublished) - new Date(a.datePublished));

  const filteredTagsList = Array.from(new Set(filteredBlogs.flatMap((blog) => (blog.tags ? blog.tags : []))));
  const filteredCategoriesList = Array.from(new Set(filteredBlogs.map((blog) => blog.category || '')));

  const filteredTags = filteredTagsList
    .filter(
      (tag) =>
        tag && typeof tag === 'string' && tag.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));

  return (
    <BlogContainer>
      <BlogIntro>
        <PageTitle>Opinions <span>& Reviews</span></PageTitle>
        <IntroText>
          A mix of book reflections, ideas, and reviews across data, business, psychology, history, and broader topics that shaped how I think.
        </IntroText>
      </BlogIntro>
      <FilterContainer>
        <DropdownContainer ref={tagDropdownRef}>
          <DropdownButton
            type="button"
            onClick={() => {
              setIsTagDropdownOpen((prev) => !prev);
              setIsCategoryDropdownOpen(false);
            }}
          >
            Filter by Tags <FiChevronDown />
          </DropdownButton>
          <DropdownMenu $isOpen={isTagDropdownOpen}>
            <DropdownSearchWrapper>
              <DropdownSearch
                type="text"
                placeholder="Search tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </DropdownSearchWrapper>
            {filteredTags.map((tag) => (
              <DropdownItem key={tag} onClick={() => handleTagSelect(tag)}>
                {tag}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </DropdownContainer>

        <DropdownContainer ref={categoryDropdownRef}>
          <DropdownButton
            type="button"
            onClick={() => {
              setIsCategoryDropdownOpen((prev) => !prev);
              setIsTagDropdownOpen(false);
            }}
          >
            Filter by Category <FiChevronDown />
          </DropdownButton>
          <DropdownMenu $isOpen={isCategoryDropdownOpen}>
            {filteredCategoriesList.map((category) => (
              <DropdownItem
                key={category}
                onClick={() => handleCategorySelect(category)}
              >
                {category}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </DropdownContainer>
      </FilterContainer>

      {(selectedTags.length > 0 || selectedCategory) && (
        <ClearButton onClick={handleClearFilters}>Clear Filters</ClearButton>
      )}

      <GridContainer>
        {filteredBlogs.map((blog, index) => (
          <BlogCard key={index} onClick={() => handleCardClick(blog.path)}>
            <BlogImage src={blog.coverImage} alt={blog.title} />
            <BlogContent>
              <BlogTitle>{blog.title}</BlogTitle>
              <BlogDescription>{blog.description}</BlogDescription>
              <DatePublished>
                <strong>Published:</strong> {blog.datePublished}
              </DatePublished>
            </BlogContent>
          </BlogCard>
        ))}
      </GridContainer>
    </BlogContainer>
  );
};

export default Blog;